<template>
  <div>
    <p v-if="loadError" role="alert">{{ loadError }}</p>
    <h1 class="mt-2 mb-4">{{ converter.name }}</h1>
    <div class="mess-box" v-if="converter.description" v-html="converter.description"></div>
    <div class="wrapper-block p-4 flex flex-column justify-content-center">
      <div
        v-for="(item, index) in converter.values_data"
        :key="index"
        class="mb-3"
      >
        <label :for="`var-${index}`" class="mb-2 name-data">{{
          item.name
        }}</label>
        <InputNumber
          :id="`var-${index}`"
          v-model="item.value"
          class="data-input"
          @input="updateValue(index, $event)"
          @update:modelValue="updateValue(index, {value: $event})"
          :min-fraction-digits="0"
          :max-fraction-digits="item.characters"
          locale="en-US"
        />
      </div>
    </div>
    <div class="mt-4 flex flex-row  justify-content-end gap-3">
      <button class="default" @click="visible = true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
        >
          <path
            d="M13.6934 12.0731C13.0179 12.0731 12.4134 12.314 11.9512 12.6915L5.61344 9.3582C5.65789 9.17346 5.69344 8.98872 5.69344 8.79595C5.69344 8.60318 5.65789 8.41844 5.61344 8.2337L11.8801 4.9325C12.3601 5.33411 12.9912 5.5831 13.6934 5.5831C15.169 5.5831 16.3601 4.5068 16.3601 3.17346C16.3601 1.84013 15.169 0.763824 13.6934 0.763824C12.2179 0.763824 11.0268 1.84013 11.0268 3.17346C11.0268 3.36623 11.0623 3.55097 11.1068 3.73571L4.84011 7.03692C4.36011 6.63531 3.729 6.38631 3.02677 6.38631C1.55122 6.38631 0.360107 7.46262 0.360107 8.79595C0.360107 10.1293 1.55122 11.2056 3.02677 11.2056C3.729 11.2056 4.36011 10.9566 4.84011 10.555L11.169 13.8964C11.1246 14.065 11.0979 14.2417 11.0979 14.4184C11.0979 15.7116 12.2623 16.7638 13.6934 16.7638C15.1246 16.7638 16.289 15.7116 16.289 14.4184C16.289 13.1253 15.1246 12.0731 13.6934 12.0731Z"
            fill="#6CB0FF"
          />
        </svg>
        Поделиться
      </button>
      <button class="default" :disabled="!online" title="Выгрузка PDF требует подключения к интернету" @click="downloadPDF">
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
  <path d="M8.21952 3.43047C11.1595 3.43047 13.5529 5.8238 13.5529 8.7638C13.5529 11.7038 11.1595 14.0971 8.21952 14.0971C5.27952 14.0971 2.88619 11.7038 2.88619 8.7638C2.88619 5.8238 5.27952 3.43047 8.21952 3.43047ZM8.21952 2.09714C4.53952 2.09714 1.55286 5.0838 1.55286 8.7638C1.55286 12.4438 4.53952 15.4305 8.21952 15.4305C11.8995 15.4305 14.8862 12.4438 14.8862 8.7638C14.8862 5.0838 11.8995 2.09714 8.21952 2.09714ZM8.88619 8.7638V6.09714H7.55286V8.7638H5.55286L8.21952 11.4305L10.8862 8.7638H8.88619Z" fill="#6CB0FF"/>
</svg>
        Скачать (PDF)
      </button>
    </div>
    <Dialog
      v-model:visible="visible"
      modal
      header="Скопируйте ссылку"
      :style="{ width: '50rem' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    >
      <div class="flex flex-row justify-content-between gap-3">
        <div class="info-link w-full">
          {{ shareableLink }}
        </div>
      </div>
      <template #footer>
        <Button
          :label="copySuccess ? 'Скопировано' : 'Скопировать'"
          :severity="copySuccess ? 'secondary' : ''"
          @click="copyToClipboard"
          :disabled="copySuccess"
        />
      </template>
    </Dialog>
    <div class="mt-8" v-if="converter.intervals">
      <h3 class="line">Референтные интервалы:</h3>
      <span class="font-light block border-round bg-white p-4 w-full mt-2 shadow-1" v-html="converter.intervals"></span>
    </div>
    <div class="mt-6" v-if="converter.links">
      <h3 class="line">Ссылки и названия ресурсов:</h3>
      <span class="font-light block border-round bg-white p-4 w-full mt-2 shadow-1" v-html="converter.links"></span>
    </div>
  </div>
</template>

<script setup>
import {online} from "@/utils/connectivity";
const loadError=ref('');
import { ref, onMounted, defineProps, computed } from "vue";
import http from '@/http';
import InputNumber from "primevue/inputnumber";
import { useRoute, useRouter } from "vue-router";
import Dialog from "primevue/dialog";
import Button from "primevue/button";


const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const converter = ref({});
const visible = ref(false);

const route = useRoute(); // Получаем текущий маршрут
const router = useRouter();

onMounted(async () => {
  try {
    const response = await http.get(
      `converters/converters/${props.id}`
    );
    converter.value = response.data;
    converter.value.values_data.forEach((element) => {
      element.value = 0;
      element.ratio = parseFloat(element.ratio);
      element.characters = parseInt(element.characters, 10);
    });

    // Проверяем наличие параметра defaultValue в URL
    const defaultValue = route.query.defaultValue;
    if (defaultValue) {
      const indexElement = converter.value.values_data.findIndex(
        (element) => element.is_index
      );
      if (indexElement !== -1) {
        // Преобразуем defaultValue в число и устанавливаем его
        const numericalValue = parseFloat(defaultValue.replace(",", "."));
        if (!isNaN(numericalValue)) {
          converter.value.values_data[indexElement].value = numericalValue;
          // Вызываем функцию пересчета значений
          recalculateValues(indexElement);
        }
      }
    }
  } catch (error) {
    loadError.value='Не удалось открыть конвертер. Подключитесь к сети и сохраните инструмент для работы офлайн.';
  }
});

const copySuccess = ref(false);

// Метод для копирования ссылки в буфер обмена
const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(shareableLink.value);
    copySuccess.value = true; // Успешно скопировано
    setTimeout(() => (copySuccess.value = false), 3000); // Сбросить статус копирования через 3 секунды
  } catch (error) {
    console.error("Copy to clipboard failed", error);
  }
};

// Вычисляемое свойство для генерации ссылки
const shareableLink = computed(() => {
  const indexElement = converter.value.values_data?.find(
    (element) => element.is_index
  );
  if (indexElement && !isNaN(indexElement.value)) {
    const baseValue = indexElement.value.toString().replace(".", ",");
    // Создаем URL, используя текущий путь и добавляем параметр defaultValue
    return `${window.location.origin}${
      router.resolve({ path: route.path, query: { defaultValue: baseValue } })
        .href
    }`;
  }
  return ""; // Если значение не установлено, возвращаем пустую строку
});

const updateValue = (index, event) => {
  if (event.value === null || event.value === '') {
    converter.value.values_data.forEach(item => { item.value = null; });
    return;
  }
  const value = Number(event.value);
  if (!Number.isFinite(value)) return;
  converter.value.values_data[index].value = value;
  recalculateValues(index);
};

const recalculateValues = (changedIndex) => {
  const valuesData = converter.value.values_data;
  let baseValue;

  // Определяем, изменено ли основное поле или второстепенное
  if (valuesData[changedIndex].is_index) {
    // Если изменено основное поле, просто берем его значение
    baseValue = valuesData[changedIndex].value;
  } else {
    // Если изменено второстепенное поле, рассчитываем значение основного поля
    baseValue = valuesData[changedIndex].value / valuesData[changedIndex].ratio;

    // Обновляем значение основного поля
    valuesData.find((element) => element.is_index).value = Number(baseValue.toFixed(
      valuesData.find((element) => element.is_index).characters
    ));
  }

  // Теперь, используя значение основного поля, пересчитываем все остальные поля
  valuesData.forEach((element) => {
    element.value = Number((baseValue * element.ratio).toFixed(element.characters));
  });
};

const downloadPDF = async () => {
  if(!online.value)return;
    try {
      // Отправляем POST-запрос на сервер
      const response = await http.post(
        "converters/generate_pdf",
        converter.value,
        { responseType: "blob" } // Указываем, что ожидаем blob в ответе
      );

      // Проверяем код ответа
      if (response.status === 200) {
        // Создаем URL для скачивания файла
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Создаем ссылку для скачивания
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Конвертер - ${converter.value.name}.pdf`);

        // Эмулируем клик по ссылке для скачивания
        link.click();

        // Освобождаем ресурсы
        window.URL.revokeObjectURL(url);
      }

      // Закрываем модальное окно
      // this.modal.hide();
    } catch (error) {
      console.error(error);
    }
  }
</script>

<style scoped>
.wrapper-block {
  width: 100%;
  min-height: 80px;
  border-radius: 5px;
  background: var(--design-color-white, #fff);
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
}

.data-input {
  width: 100%;
}
.name-data {
  font-weight: 500;
}
</style>
